import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { SuggestionDataItem } from 'react-mentions';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import CommentContainer from '@buyside/components/container/CommentContainer';
import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';
import PostCommentForm from '@buyside/components/presentation/PostShow/postCommentForm';

import { usePostCommentsQuery } from '@interfaces/graphql-typings';

import styles from './postShowStyles.scss';

const PostComments: React.FC<{ postId: string }> = ({ postId }): React.ReactElement => {
  // -*-*-*-*-* sticky stuff *-*-*-*-*-
  const [topOffset, setTopOffset] = useState<null | number>(null);
  const [isSticky, updateSticky] = useState(false);

  useEffect(() => {
    const topCoordinate = document.getElementById('commentForm')?.getBoundingClientRect()?.top;
    if (topCoordinate && topCoordinate > 0) setTopOffset(topCoordinate);
  }, []);

  useEffect(() => {
    // Disable sticky for mobile devices
    if (!topOffset || window.innerWidth < 1025) return;

    const handleScroll = (): void => updateSticky(window.pageYOffset > topOffset);

    window.addEventListener('scroll', handleScroll);
    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [topOffset, updateSticky]);
  // -*-*-*-*-*--*-*-*-*-*--*-*-*-*-*--*-*-*-*-

  const { data, loading, error, fetchMore } = usePostCommentsQuery({ variables: { postId } });

  if (loading || !data) return <LoadingSpinner size={SIZES.LARGE} />;

  if (error || !data.post || !data.currentUser) throw error;

  const { comments, commentsCount, commentMentionables } = data.post;
  const { isAllocator, isRetail, canCommentAnonymously, profileImageUrl } = data.currentUser;

  const allComments = comments?.edges?.map(e => e?.node || null) || [];

  return (
    <>
      {!isRetail && (
        <div
          id="commentForm"
          className={classnames(styles.commentInput, { [styles.formSticky]: isSticky })}
        >
          <PostCommentForm
            canCommentAnonymously={canCommentAnonymously}
            mentionables={commentMentionables as SuggestionDataItem[]}
            isAllocator={isAllocator}
            isChildComment={false}
            parentId={null}
            postId={postId}
            profileImageUrl={profileImageUrl}
          />
        </div>
      )}
      <div className={styles.commentCount}>
        {commentsCount || 0} Comment{commentsCount === 1 ? '' : 's'}
      </div>
      {allComments.length ? (
        <div className={styles.commentList}>
          {allComments.map(
            node =>
              node &&
              !node.parentId && (
                <CommentContainer
                  comment={node}
                  allComments={allComments}
                  key={node.id}
                  showLikes
                  canCommentAnonymously={canCommentAnonymously}
                  mentionables={commentMentionables as SuggestionDataItem[]}
                  isAllocator={isAllocator}
                  postId={postId}
                  profileImageUrl={profileImageUrl}
                />
              )
          )}
          {comments?.pageInfo.hasNextPage && comments.pageInfo.endCursor && (
            <Button
              id="load-more-comments"
              onClick={(): void => {
                fetchMore({ variables: { cursor: comments.pageInfo.endCursor, postId } });
              }}
              text="Load older comments"
              type={BUTTON_TYPES.LINK}
            />
          )}
        </div>
      ) : null}
    </>
  );
};

export default PostComments;
